import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  V2Badge,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2FormField,
  V2FormGrid,
  LoadingState,
  NoticeBanner,
  V2CloseButton,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiPost } from '../../lib/apiClient.js';
import { bannerMessageFrom } from '../../lib/formErrors.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  BankReconciliationSessionDto,
  BankStatementImportDto,
  BankStatementLineDto,
} from '../../lib/types/finance.js';

interface BankAccountDetail {
  readonly bankAccountId: string;
  readonly bankName: string;
  readonly branchName: string | null;
  readonly branchCode: string | null;
  readonly accountNumber: string;
  readonly iban: string | null;
  readonly accountType: string;
  readonly status: string;
}

interface BankAccountsResponse {
  readonly items: readonly BankAccountDetail[];
}

interface TreasuryTransactionsResponse {
  readonly items: readonly {
    readonly treasuryTransactionId: string;
    readonly kind: string;
    readonly sourceReference: string;
    readonly businessDate: string;
    readonly amountRial: string;
    readonly status: string;
  }[];
}

const SAMPLE_CSV = `rowNumber,transactionDate,referenceNumber,description,debitRial,creditRial,balanceRial
1,1403-07-01,REF-1001,واریز بابت فروش,500000000,0,500000000
2,1403-07-04,REF-1002,پرداخت به تأمین‌کننده,0,150000000,350000000
3,1403-07-12,REF-1003,واریز تسویه مشتری,200000000,0,550000000
4,1403-07-20,REF-1004,هزینه کارمزد بانکی,0,500000,549500000`;

export function BankReconciliationPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { unit } = useAmountDisplay();

  const [selectedBankAccountId, setSelectedBankAccountId] = useState('');
  const [activeImport, setActiveImport] = useState<BankStatementImportDto | null>(null);
  const [activeTab, setActiveTab] = useState<'matching' | 'sessions'>('matching');
  const [matchFilter, setMatchFilter] = useState<'all' | 'matched' | 'unmatched'>('all');
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'info' | 'warning'; text: string } | null>(null);

  // Import Modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFileName, setImportFileName] = useState('صورتحساب_بانک.csv');
  const [importFromDate, setImportFromDate] = useState('1403-07-01');
  const [importToDate, setImportToDate] = useState('1403-07-30');
  const [importOpeningBalance, setImportOpeningBalance] = useState('0');
  const [importClosingBalance, setImportClosingBalance] = useState('549500000');
  const [csvContent, setCsvContent] = useState(SAMPLE_CSV);

  // Session Modal state
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionAsOfDate, setSessionAsOfDate] = useState('1403-07-30');
  const [sessionNotes, setSessionNotes] = useState('');

  // Manual match modal state
  const [selectedLineForMatch, setSelectedLineForMatch] = useState<BankStatementLineDto | null>(
    null,
  );
  const [selectedTreasuryTxnId, setSelectedTreasuryTxnId] = useState('');

  // 1. Fetch bank accounts
  const bankAccounts = useApiGet<BankAccountsResponse>(
    slug ? `${base}/api/v1/finance/treasury/bank-accounts` : null,
    [slug],
  );

  // Auto-select first bank account
  useMemo(() => {
    if (!selectedBankAccountId && bankAccounts.status === 'ready' && bankAccounts.data.items[0]) {
      setSelectedBankAccountId(bankAccounts.data.items[0].bankAccountId);
    }
  }, [bankAccounts, selectedBankAccountId]);

  // Current selected bank account detail
  const currentBankAccount = useMemo(() => {
    if (bankAccounts.status !== 'ready') return null;
    return bankAccounts.data.items.find((a) => a.bankAccountId === selectedBankAccountId) ?? null;
  }, [bankAccounts, selectedBankAccountId]);

  // 3. Fetch statement lines if an import is active
  const linesQuery =
    matchFilter === 'matched'
      ? '?isMatched=true'
      : matchFilter === 'unmatched'
        ? '?isMatched=false'
        : '';
  const linesResult = useApiGet<{ items: readonly BankStatementLineDto[] }>(
    slug && activeImport
      ? `${base}/api/v1/finance/treasury/bank-reconciliation/imports/${activeImport.statementImportId}/lines${linesQuery}`
      : null,
    [slug, activeImport?.statementImportId, matchFilter, refreshKey],
  );

  // 4. Fetch reconciliation sessions for selected bank account
  const sessionsResult = useApiGet<{ items: readonly BankReconciliationSessionDto[] }>(
    slug && selectedBankAccountId
      ? `${base}/api/v1/finance/treasury/bank-reconciliation/accounts/${selectedBankAccountId}/sessions`
      : null,
    [slug, selectedBankAccountId, refreshKey],
  );

  // 5. Fetch treasury transactions for manual matching helper
  const treasuryTxns = useApiGet<TreasuryTransactionsResponse>(
    slug && showImportModal === false && selectedLineForMatch !== null
      ? `${base}/api/v1/finance/treasury/transactions`
      : null,
    [slug, selectedLineForMatch],
  );

  if (!slug) return null;

  function parseCsvLines(rawText: string) {
    const lines = rawText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length <= 1) return [];

    const parsed = [];

    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i]!.split(',').map((p) => p.trim());
      if (parts.length < 5) continue;

      const rowNumber = parseInt(parts[0] || `${i}`, 10) || i;
      const transactionDate = parts[1] || '1403-01-01';
      const referenceNumber = parts[2] || undefined;
      const description = parts[3] || 'تراکنش بانکی';
      const debitRial = parts[4] && parts[4] !== '0' ? parts[4] : undefined;
      const creditRial = parts[5] && parts[5] !== '0' ? parts[5] : undefined;
      const balanceRial = parts[6] || parts[4] || '0';

      parsed.push({
        rowNumber,
        transactionDate,
        referenceNumber,
        description,
        debitRial,
        creditRial,
        balanceRial,
      });
    }
    return parsed;
  }

  async function onImportStatement() {
    if (!selectedBankAccountId) {
      setMessage({ tone: 'warning', text: 'لطفاً ابتدا حساب بانکی را انتخاب کنید.' });
      return;
    }
    const lines = parseCsvLines(csvContent);
    if (lines.length === 0) {
      setMessage({ tone: 'warning', text: 'هیچ سطری در فایل صورتحساب معتبر شناخته نشد.' });
      return;
    }

    setBusy(true);
    setMessage(null);
    try {
      const res = await apiPost<BankStatementImportDto>(
        `${base}/api/v1/finance/treasury/bank-reconciliation/imports`,
        {
          bankAccountId: selectedBankAccountId,
          fileName: importFileName,
          format: 'csv',
          fromDate: importFromDate,
          toDate: importToDate,
          openingBalanceRial: importOpeningBalance,
          closingBalanceRial: importClosingBalance,
          lines,
        },
      );
      setActiveImport(res);
      setShowImportModal(false);
      setMessage({
        tone: 'info',
        text: `فایل صورتحساب بانکی با ${res.totalLinesCount.toLocaleString('fa-IR')} سطر با موفقیت ایمپورت شد.`,
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setMessage({ tone: 'warning', text: bannerMessageFrom(err, 'ایمپورت صورتحساب ناموفق بود.') });
    } finally {
      setBusy(false);
    }
  }

  async function onAutoMatch() {
    if (!activeImport) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await apiPost<{ matchedCount: number; message: string }>(
        `${base}/api/v1/finance/treasury/bank-reconciliation/imports/${activeImport.statementImportId}/auto-match`,
        {},
      );
      setMessage({
        tone: 'info',
        text: res.message || `${res.matchedCount} تراکنش به صورت خودکار تطبیق یافت.`,
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setMessage({
        tone: 'warning',
        text: bannerMessageFrom(err, 'تطبیق خودکار با خطا مواجه شد.'),
      });
    } finally {
      setBusy(false);
    }
  }

  async function onManualMatchSubmit() {
    if (!selectedLineForMatch || !selectedTreasuryTxnId) {
      setMessage({ tone: 'warning', text: 'انتخاب تراکنش خزانه جهت تطبیق الزامی است.' });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await apiPost(`${base}/api/v1/finance/treasury/bank-reconciliation/matches/manual`, {
        statementLineId: selectedLineForMatch.statementLineId,
        treasuryTransactionId: selectedTreasuryTxnId,
      });
      setSelectedLineForMatch(null);
      setSelectedTreasuryTxnId('');
      setMessage({ tone: 'info', text: 'تطبیق دستی سطر با موفقیت ثبت شد.' });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setMessage({ tone: 'warning', text: bannerMessageFrom(err, 'تطبیق دستی ناموفق بود.') });
    } finally {
      setBusy(false);
    }
  }

  async function onCreateSession() {
    if (!selectedBankAccountId) return;
    setBusy(true);
    setMessage(null);
    try {
      await apiPost(`${base}/api/v1/finance/treasury/bank-reconciliation/sessions`, {
        bankAccountId: selectedBankAccountId,
        statementImportId: activeImport?.statementImportId,
        asOfDate: sessionAsOfDate,
        notes: sessionNotes.trim() || undefined,
      });
      setShowSessionModal(false);
      setSessionNotes('');
      setActiveTab('sessions');
      setMessage({ tone: 'info', text: 'صورت مغایرت بانکی با موفقیت تهیه و ثبت شد.' });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setMessage({ tone: 'warning', text: bannerMessageFrom(err, 'ثبت صورت مغایرت ناموفق بود.') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* HEADER CARD: BANK ACCOUNT SELECTOR & SUMMARY */}
      <V2Card>
        <V2CardHeader
          title="مغایرت بانکی و صورتحساب (Bank Reconciliation)"
          subtitle="ایمپورت فایل صورتحساب بانکی، تطبیق هوشمند با اسناد خزانه و تهیه صورت مغایرت"
          actions={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <V2Button size="sm" variant="secondary" onClick={() => setShowImportModal(true)}>
                + ایمپورت فایل صورتحساب (Excel / CSV)
              </V2Button>
              <V2Button size="sm" variant="primary" onClick={() => setShowSessionModal(true)}>
                ثبت صورت مغایرت بانکی
              </V2Button>
            </div>
          }
        />

        <div
          style={{
            padding: '0 20px 18px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 16,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ minWidth: 280 }}>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                color: 'var(--sk-fg-muted)',
                marginBottom: 4,
              }}
            >
              انتخاب حساب بانکی:
            </label>
            <select
              className="sk-select"
              value={selectedBankAccountId}
              onChange={(e) => {
                setSelectedBankAccountId(e.target.value);
                setActiveImport(null);
              }}
            >
              <option value="">انتخاب حساب بانکی...</option>
              {bankAccounts.status === 'ready' &&
                bankAccounts.data.items.map((b) => (
                  <option key={b.bankAccountId} value={b.bankAccountId}>
                    {b.bankName} — شماره حساب: {b.accountNumber}{' '}
                    {b.branchName ? `(${b.branchName})` : ''}
                  </option>
                ))}
            </select>
          </div>

          {currentBankAccount ? (
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13 }}>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>شبا: </span>
                <span className="ltr sk-num" style={{ fontWeight: 600 }}>
                  {currentBankAccount.iban ?? '—'}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>شعبه: </span>
                <span>
                  {currentBankAccount.branchName ?? 'مرکزی'} ({currentBankAccount.branchCode ?? '—'}
                  )
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--sk-fg-muted)' }}>وضعیت حساب: </span>
                <V2Badge tone="success">فعال در خزانه</V2Badge>
              </div>
            </div>
          ) : null}
        </div>
      </V2Card>

      {message ? <NoticeBanner tone={message.tone}>{message.text}</NoticeBanner> : null}

      {/* TABS SWITCHER */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          borderBottom: '1px solid var(--sk-divider)',
          paddingBottom: 8,
        }}
      >
        <button
          type="button"
          className={`sk-btn ${activeTab === 'matching' ? 'sk-btn--primary' : 'sk-btn--secondary'} sk-btn--sm`}
          onClick={() => setActiveTab('matching')}
        >
          اقلام صورتحساب و تطبیق هوشمند
        </button>
        <button
          type="button"
          className={`sk-btn ${activeTab === 'sessions' ? 'sk-btn--primary' : 'sk-btn--secondary'} sk-btn--sm`}
          onClick={() => setActiveTab('sessions')}
        >
          جلسات و صورت‌های مغایرت بانکی (
          {sessionsResult.status === 'ready'
            ? sessionsResult.data.items.length.toLocaleString('fa-IR')
            : 0}
          )
        </button>
      </div>

      {/* TAB 1: STATEMENT LINES & MATCHING */}
      {activeTab === 'matching' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {activeImport ? (
            <V2Card>
              <V2CardHeader
                title={`صورتحساب فعال: ${activeImport.fileName}`}
                subtitle={`بازه: ${displayJalaliDate(activeImport.fromDate)} تا ${displayJalaliDate(activeImport.toDate)} · تعداد کل: ${activeImport.totalLinesCount.toLocaleString('fa-IR')} سطر · مانده دفتری: ${formatRialFa(activeImport.closingBalanceRial, unit)}`}
                actions={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        className={`sk-btn ${matchFilter === 'all' ? 'sk-btn--primary' : 'sk-btn--ghost'} sk-btn--sm`}
                        onClick={() => setMatchFilter('all')}
                      >
                        همه
                      </button>
                      <button
                        type="button"
                        className={`sk-btn ${matchFilter === 'matched' ? 'sk-btn--primary' : 'sk-btn--ghost'} sk-btn--sm`}
                        onClick={() => setMatchFilter('matched')}
                      >
                        تطبیق‌یافته
                      </button>
                      <button
                        type="button"
                        className={`sk-btn ${matchFilter === 'unmatched' ? 'sk-btn--primary' : 'sk-btn--ghost'} sk-btn--sm`}
                        onClick={() => setMatchFilter('unmatched')}
                      >
                        مغایرت‌ها
                      </button>
                    </div>

                    <V2Button
                      size="sm"
                      variant="primary"
                      disabled={busy}
                      onClick={() => void onAutoMatch()}
                    >
                      ⚡ اجرای تطبیق خودکار هوشمند
                    </V2Button>
                  </div>
                }
              />

              {linesResult?.status === 'loading' ? <LoadingState /> : null}
              {linesResult?.status === 'ready' && linesResult.data.items.length === 0 ? (
                <div style={{ padding: 20 }}>
                  <V2EmptyState icon="finance" title="سطری با فیلتر انتخابی یافت نشد" />
                </div>
              ) : null}

              {linesResult?.status === 'ready' && linesResult.data.items.length > 0 ? (
                <div className="sk-table-scroll">
                  <table className="sk-table sk-table--compact">
                    <thead>
                      <tr>
                        <th>ردیف</th>
                        <th>تاریخ</th>
                        <th>شماره پیگیری / مرجع</th>
                        <th>شرح تراکنش</th>
                        <th style={{ textAlign: 'end' }}>واریز (بدهکار)</th>
                        <th style={{ textAlign: 'end' }}>برداشت (بستانکار)</th>
                        <th style={{ textAlign: 'end' }}>مانده</th>
                        <th>وضعیت تطبیق</th>
                        <th>اقدام</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linesResult.data.items.map((line) => (
                        <tr key={line.statementLineId}>
                          <td className="ltr sk-num">{line.rowNumber}</td>
                          <td>{displayJalaliDate(line.transactionDate)}</td>
                          <td className="ltr sk-num" style={{ fontWeight: 600 }}>
                            {line.referenceNumber ?? '—'}
                          </td>
                          <td style={{ fontSize: 12.5 }}>{line.description}</td>
                          <td
                            className="sk-num"
                            style={{
                              textAlign: 'end',
                              color: line.debitRial !== '0' ? 'var(--sk-credit)' : undefined,
                            }}
                          >
                            {line.debitRial !== '0' ? formatRialFa(line.debitRial, unit) : '—'}
                          </td>
                          <td
                            className="sk-num"
                            style={{
                              textAlign: 'end',
                              color: line.creditRial !== '0' ? 'var(--sk-danger)' : undefined,
                            }}
                          >
                            {line.creditRial !== '0' ? formatRialFa(line.creditRial, unit) : '—'}
                          </td>
                          <td className="sk-num" style={{ textAlign: 'end', fontWeight: 700 }}>
                            {formatRialFa(line.balanceRial, unit)}
                          </td>
                          <td>
                            <V2Badge tone={line.isMatched ? 'success' : 'warn'}>
                              {line.isMatched ? 'تطبیق یافته' : 'مغایرت'}
                            </V2Badge>
                          </td>
                          <td>
                            {!line.isMatched ? (
                              <button
                                type="button"
                                className="sk-btn sk-btn--ghost sk-btn--sm"
                                onClick={() => {
                                  setSelectedLineForMatch(line);
                                  setSelectedTreasuryTxnId('');
                                }}
                              >
                                تطبیق دستی
                              </button>
                            ) : (
                              <span style={{ fontSize: 11, color: 'var(--sk-fg-muted)' }}>
                                ثبت‌شده
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </V2Card>
          ) : (
            <V2Card>
              <div style={{ padding: 32, textAlign: 'center' }}>
                <V2EmptyState
                  icon="finance"
                  title="هیچ صورتحساب فعالی انتخاب یا ایمپورت نشده است"
                  action={
                    <V2Button size="sm" variant="primary" onClick={() => setShowImportModal(true)}>
                      ایمپورت فایل صورتحساب بانکی (Excel / CSV)
                    </V2Button>
                  }
                />
              </div>
            </V2Card>
          )}
        </div>
      ) : null}

      {/* TAB 2: RECONCILIATION SESSIONS */}
      {activeTab === 'sessions' ? (
        <V2Card>
          <V2CardHeader
            title="جلسات و صورت‌های مغایرت بانکی ثبت‌شده"
            subtitle="تاریخچه مقایسه مانده دفاتر حسابداری با مانده صورتحساب بانک و استخراج اقلام باز"
          />

          {sessionsResult.status === 'loading' ? <LoadingState /> : null}
          {sessionsResult.status === 'ready' && sessionsResult.data.items.length === 0 ? (
            <div style={{ padding: 24 }}>
              <V2EmptyState
                icon="finance"
                title="تاکنون جلسه مغایرت‌گیری برای این حساب ثبت نشده است"
                action={
                  <V2Button size="sm" variant="primary" onClick={() => setShowSessionModal(true)}>
                    تهیه و ثبت اولین صورت مغایرت
                  </V2Button>
                }
              />
            </div>
          ) : null}

          {sessionsResult.status === 'ready' && sessionsResult.data.items.length > 0 ? (
            <div className="sk-table-scroll">
              <table className="sk-table sk-table--compact">
                <thead>
                  <tr>
                    <th>تاریخ مغایرت</th>
                    <th style={{ textAlign: 'end' }}>مانده دفاتر مالی (GL)</th>
                    <th style={{ textAlign: 'end' }}>مانده صورتحساب بانک</th>
                    <th style={{ textAlign: 'end' }}>مبلغ انحراف / مغایرت</th>
                    <th>وضعیت تراز</th>
                    <th>یادداشت‌ها</th>
                    <th>تاریخ ثبت</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionsResult.data.items.map((s, idx) => {
                    const diffRaw = s.differenceRial ?? s.discrepancyRial ?? '0';
                    let isBalanced = false;
                    try {
                      isBalanced = BigInt(diffRaw) === 0n;
                    } catch {
                      isBalanced = diffRaw === '0';
                    }
                    const glRaw = s.glBalanceRial ?? s.glBookBalanceRial ?? '0';
                    const stmtRaw = s.statementBalanceRial ?? s.bankStatementBalanceRial ?? '0';
                    const rowKey = s.reconciliationId ?? s.sessionId ?? `session-${idx}`;
                    return (
                      <tr key={rowKey}>
                        <td style={{ fontWeight: 600 }}>{displayJalaliDate(s.asOfDate)}</td>
                        <td className="sk-num" style={{ textAlign: 'end' }}>
                          {formatRialFa(glRaw, unit)}
                        </td>
                        <td className="sk-num" style={{ textAlign: 'end' }}>
                          {formatRialFa(stmtRaw, unit)}
                        </td>
                        <td
                          className="sk-num"
                          style={{
                            textAlign: 'end',
                            fontWeight: 800,
                            color: isBalanced ? 'var(--sk-credit)' : 'var(--sk-danger)',
                          }}
                        >
                          {formatRialFa(diffRaw, unit)}
                        </td>
                        <td>
                          <V2Badge tone={isBalanced ? 'success' : 'warn'}>
                            {isBalanced ? 'متوازن بدون مغایرت' : 'دارای اقلام باز'}
                          </V2Badge>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                          {s.notes ?? '—'}
                        </td>
                        <td style={{ fontSize: 12 }}>{displayJalaliDate(s.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </V2Card>
      ) : null}

      {/* MODAL 1: IMPORT BANK STATEMENT */}
      {showImportModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'var(--sk-bg)',
              borderRadius: 8,
              padding: 24,
              width: '100%',
              maxWidth: 620,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowImportModal(false)} />
            </div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              ایمپورت فایل صورتحساب بانکی (Excel / CSV)
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              می‌توانید داده‌های فایل اکسل یا CSV صادره از سامانه بانک (ملی، ملت، سامان، پاسارگاد
              و...) را وارد کنید.
            </p>

            <V2FormGrid>
              <V2FormField label="نام فایل" required width="medium">
                <input
                  className="sk-input"
                  value={importFileName}
                  onChange={(e) => setImportFileName(e.target.value)}
                />
              </V2FormField>
              <V2FormField label="از تاریخ" required width="medium">
                <JalaliDateInput value={importFromDate} onChange={setImportFromDate} />
              </V2FormField>
              <V2FormField label="تا تاریخ" required width="medium">
                <JalaliDateInput value={importToDate} onChange={setImportToDate} />
              </V2FormField>
              <V2FormField label="مانده ابتدا دوره (ریال)" required width="medium">
                <input
                  className="sk-input ltr"
                  value={importOpeningBalance}
                  onChange={(e) => setImportOpeningBalance(e.target.value)}
                />
              </V2FormField>
              <V2FormField label="مانده انتهای دوره (ریال)" required width="medium">
                <input
                  className="sk-input ltr"
                  value={importClosingBalance}
                  onChange={(e) => setImportClosingBalance(e.target.value)}
                />
              </V2FormField>
            </V2FormGrid>

            <div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
              >
                <label style={{ fontSize: 12, fontWeight: 600 }}>
                  محتوای CSV / سطرهای صورتحساب:
                </label>
                <button
                  type="button"
                  className="sk-btn sk-btn--ghost sk-btn--sm"
                  onClick={() => setCsvContent(SAMPLE_CSV)}
                >
                  بارگذاری پیش‌فرض نمونه
                </button>
              </div>
              <textarea
                className="sk-input ltr"
                style={{ width: '100%', height: 130, fontFamily: 'monospace', fontSize: 12 }}
                value={csvContent}
                onChange={(e) => setCsvContent(e.target.value)}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="sk-btn sk-btn--ghost sk-btn--sm"
                disabled={busy}
                onClick={() => setShowImportModal(false)}
              >
                انصراف
              </button>
              <button
                type="button"
                className="sk-btn sk-btn--primary sk-btn--sm"
                disabled={busy}
                onClick={() => void onImportStatement()}
              >
                {busy ? 'در حال پردازش و ثبت...' : 'تأیید و ایمپورت صورتحساب'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* MODAL 2: MANUAL MATCH */}
      {selectedLineForMatch ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'var(--sk-bg)',
              borderRadius: 8,
              padding: 24,
              width: '100%',
              maxWidth: 520,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setSelectedLineForMatch(null)} />
            </div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              تطبیق دستی سطر صورتحساب با سند خزانه
            </h3>
            <div
              style={{
                background: 'var(--sk-bg-subtle)',
                padding: 12,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              <div>
                <strong>تاریخ:</strong> {displayJalaliDate(selectedLineForMatch.transactionDate)}
              </div>
              <div>
                <strong>شماره پیگیری:</strong> {selectedLineForMatch.referenceNumber ?? 'ندارد'}
              </div>
              <div>
                <strong>شرح:</strong> {selectedLineForMatch.description}
              </div>
              <div>
                <strong>مبلغ:</strong>{' '}
                {formatRialFa(
                  selectedLineForMatch.debitRial !== '0'
                    ? selectedLineForMatch.debitRial
                    : selectedLineForMatch.creditRial,
                  unit,
                )}
              </div>
            </div>

            <V2FormField label="انتخاب تراکنش متناظر از اسناد خزانه" required width="wide">
              <select
                className="sk-select"
                value={selectedTreasuryTxnId}
                onChange={(e) => setSelectedTreasuryTxnId(e.target.value)}
              >
                <option value="">انتخاب سند خزانه...</option>
                {treasuryTxns.status === 'ready' &&
                  treasuryTxns.data.items.map((t) => (
                    <option key={t.treasuryTransactionId} value={t.treasuryTransactionId}>
                      {displayJalaliDate(t.businessDate)} — مرجع: {t.sourceReference || '—'} —{' '}
                      {formatRialFa(t.amountRial, unit)} (
                      {t.kind === 'receipt' ? 'دریافت' : 'پرداخت'})
                    </option>
                  ))}
              </select>
            </V2FormField>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="sk-btn sk-btn--ghost sk-btn--sm"
                disabled={busy}
                onClick={() => setSelectedLineForMatch(null)}
              >
                انصراف
              </button>
              <button
                type="button"
                className="sk-btn sk-btn--primary sk-btn--sm"
                disabled={busy || !selectedTreasuryTxnId}
                onClick={() => void onManualMatchSubmit()}
              >
                {busy ? 'در حال ثبت...' : 'تأیید تطبیق دستی'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* MODAL 3: CREATE RECONCILIATION SESSION */}
      {showSessionModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'var(--sk-bg)',
              borderRadius: 8,
              padding: 24,
              width: '100%',
              maxWidth: 480,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', margin: '-10px -10px -6px' }}
            >
              <V2CloseButton onClick={() => setShowSessionModal(false)} />
            </div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
              تهیه و ثبت صورت مغایرت بانکی
            </h3>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--sk-fg-muted)' }}>
              موتور حسابداری مانده حساب معین بانکی در دفاتر مالی را در تاریخ مشخص شده با مانده
              صورتحساب بانکی مقایسه کرده و انحراف را محاسبه می‌کند.
            </p>

            <V2FormField label="تا تاریخ (تاریخ مقایسه و تراز)" required width="wide">
              <JalaliDateInput value={sessionAsOfDate} onChange={setSessionAsOfDate} />
            </V2FormField>

            <V2FormField label="یادداشت و توضیحات حسابرس" width="wide">
              <textarea
                className="sk-input"
                style={{ width: '100%', height: 70 }}
                placeholder="توضیحات اختیاری درباره اقلام باز و پیگیری‌ها..."
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
              />
            </V2FormField>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="sk-btn sk-btn--ghost sk-btn--sm"
                disabled={busy}
                onClick={() => setShowSessionModal(false)}
              >
                انصراف
              </button>
              <button
                type="button"
                className="sk-btn sk-btn--primary sk-btn--sm"
                disabled={busy}
                onClick={() => void onCreateSession()}
              >
                {busy ? 'در حال استخراج و محاسبه...' : 'محاسبه و ثبت صورت مغایرت'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
